import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import FieldHelpMark from '@/components/FieldHelpMark';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import PublishImageUploadZone from '@/temu-platform/modules/products/components/PublishImageUploadZone';
import { toIpeUserErrorMessage } from '@/utils/ipeErrorSanitizer';

/** 平台 modelType：0 成衣 / 1 鞋模 */
const MODEL_TYPE_CLOTHES = 0;
const MODEL_TYPE_SHOE = 1;

/** 平台：模特名称仅支持英文、数字、. 与 - */
const MODEL_NAME_PATTERN = /^[A-Za-z0-9.-]+$/;
const IS_MEASURE = (v) => /^\d+(\.\d+)?$/.test(String(v || '').trim());

function pickClothes(initial) {
  const clothes = initial?.clothesModel || initial?.payload?.clothesModel || {};
  return {
    height: String(clothes.height ?? initial?.height ?? initial?.payload?.modelHeight ?? '').trim(),
    bust: String(clothes.bust ?? initial?.bust ?? initial?.payload?.modelBust ?? '').trim(),
    waist: String(clothes.waist ?? initial?.waist ?? initial?.payload?.modelWaist ?? '').trim(),
    hipline: String(
      clothes.hipline ?? initial?.hip ?? initial?.payload?.modelHip ?? '',
    ).trim(),
  };
}

function pickShoe(initial) {
  const shoe = initial?.shoeModel || initial?.payload?.shoeModel || {};
  return {
    footLength: String(
      shoe.footLength ?? initial?.footLength ?? initial?.payload?.modelFootLength ?? '',
    ).trim(),
    footWidth: String(
      shoe.footWidth ?? initial?.footWidth ?? initial?.payload?.modelFootWidth ?? '',
    ).trim(),
  };
}

function resolveModelType(initial) {
  const raw = initial?.modelType ?? initial?.payload?.modelType;
  if (raw === 0 || raw === '0') return MODEL_TYPE_CLOTHES;
  if (raw === 1 || raw === '1') return MODEL_TYPE_SHOE;
  // 发品侧曾用 1=成衣 / 2=鞋模
  if (raw === 2 || raw === '2') return MODEL_TYPE_SHOE;
  return MODEL_TYPE_CLOTHES;
}

function portraitUrlOf(initial) {
  return String(
    initial?.headPortrait
    || initial?.thumbUrl
    || initial?.modelProfileUrl
    || initial?.payload?.headPortrait
    || initial?.payload?.modelProfileUrl
    || '',
  ).trim();
}

/** 已有平台头像 URL → 上传槽位（编辑回显） */
function portraitSlotFromUrl(url) {
  const src = String(url || '').trim();
  if (!src) return null;
  return {
    id: `existing-${src.slice(-24)}`,
    name: i18n.t('modelLib.avatar', { ns: 'workbench' }),
    previewUrl: src,
    publicUrl: src,
    ipeUrl: src,
    temuUrl: src,
  };
}

function slotPortraitUrl(slot) {
  if (!slot || typeof slot !== 'object') return '';
  return String(
    slot.temuUrl
    || slot.ipeUrl
    || slot.publicUrl
    || slot.previewUrl
    || slot.url
    || slot.urls?.[0]
    || '',
  ).trim();
}

function slotTemuUrl(slot) {
  if (!slot || typeof slot !== 'object') return '';
  return String(slot.temuUrl || slot.url || slot.urls?.[0] || '').trim();
}

function FieldError({ message }) {
  if (!message) return null;
  return <div className="tpo-field__error" role="alert">{message}</div>;
}

/** 去掉 Temu 前缀 / 平台 code / type，便于字段下展示 */
function cleanPlatformFieldMessage(raw) {
  let s = String(raw || '').trim();
  s = s.replace(/^Temu[：:]\s*/i, '');
  s = s.replace(/（?\s*平台\s*code\s*=\s*\d+\s*）?/gi, '');
  s = s.replace(/\s*type\s*=\s*\S+/gi, '');
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s || i18n.t('modelLib.invalidPlatform', { ns: 'workbench' });
}

/**
 * 将平台/本地校验文案映射到表单字段。
 * @returns {Record<string, string>}
 */
function mapModelErrorToFields(rawMessage) {
  const full = String(rawMessage || '').trim();
  if (!full) return { form: i18n.t('modelLib.saveRetry', { ns: 'workbench' }) };
  const msg = cleanPlatformFieldMessage(full);
  const errors = {};

  if (/模特名称|姓名|model\s*name|仅支持.*英文|仅支持\s*\.\s*-数字/i.test(full)) {
    errors.modelName = msg;
  }
  if (/头像|headPortrait|portrait|图片/i.test(full) && !/名称|姓名/i.test(full)) {
    errors.headPortrait = msg;
  }
  if (/身高|height/i.test(full)) errors.height = msg;
  if (/胸围|bust/i.test(full)) errors.bust = msg;
  if (/腰围|waist/i.test(full)) errors.waist = msg;
  if (/臀围|hipline|hip/i.test(full)) errors.hipline = msg;
  if (/脚长|footLength|foot\s*length/i.test(full)) errors.footLength = msg;
  if (/脚宽|footWidth|foot\s*width/i.test(full)) errors.footWidth = msg;
  if (/类型|modelType/i.test(full) && !errors.modelName) {
    errors.modelType = msg;
  }

  if (!Object.keys(errors).length) {
    errors.form = msg;
  }
  return errors;
}

function validateModelForm({
  modelName,
  modelType,
  height,
  bust,
  waist,
  hipline,
  footLength,
  footWidth,
}) {
  const errors = {};
  const name = String(modelName || '').trim();
  if (!name) {
    errors.modelName = i18n.t('modelLib.needName', { ns: 'workbench' });
  } else if (name.length > 64) {
    errors.modelName = i18n.t('modelLib.nameTooLong', { ns: 'workbench' });
  } else if (!MODEL_NAME_PATTERN.test(name)) {
    errors.modelName = i18n.t('modelLib.nameCharset', { ns: 'workbench' });
  }

  const type = Number(modelType);
  if (type === MODEL_TYPE_CLOTHES) {
    const h = String(height || '').trim();
    const b = String(bust || '').trim();
    const w = String(waist || '').trim();
    const hip = String(hipline || '').trim();
    if (!h) errors.height = i18n.t('modelLib.needHeight', { ns: 'workbench' });
    else if (!IS_MEASURE(h)) errors.height = i18n.t('modelLib.heightNumber', { ns: 'workbench' });
    if (!b) errors.bust = i18n.t('modelLib.needBust', { ns: 'workbench' });
    else if (!IS_MEASURE(b)) errors.bust = i18n.t('modelLib.bustNumber', { ns: 'workbench' });
    if (!w) errors.waist = i18n.t('modelLib.needWaist', { ns: 'workbench' });
    else if (!IS_MEASURE(w)) errors.waist = i18n.t('modelLib.waistNumber', { ns: 'workbench' });
    if (!hip) errors.hipline = i18n.t('modelLib.needHip', { ns: 'workbench' });
    else if (!IS_MEASURE(hip)) errors.hipline = i18n.t('modelLib.hipNumber', { ns: 'workbench' });
  } else {
    const fl = String(footLength || '').trim();
    const fw = String(footWidth || '').trim();
    if (!fl) errors.footLength = i18n.t('modelLib.needFootLen', { ns: 'workbench' });
    else if (!IS_MEASURE(fl)) errors.footLength = i18n.t('modelLib.footLenNumber', { ns: 'workbench' });
    if (!fw) errors.footWidth = i18n.t('modelLib.needFootW', { ns: 'workbench' });
    else if (!IS_MEASURE(fw)) errors.footWidth = i18n.t('modelLib.footWNumber', { ns: 'workbench' });
  }
  return errors;
}

/**
 * 模特库 · 新建 / 编辑。
 * - 店铺模式：写入平台 bg.glo.modelinfo.add / edit
 * - 官方模式（officialMode）：写入 Admin 官方模特库，头像落 IPE 存储
 */
export default function ModelTemplateCreateModal({
  open,
  shopId,
  officialMode = false,
  initial = null,
  /** 新建时默认类型：0 成衣 / 1 鞋类（编辑仍以 initial 为准） */
  defaultModelType = MODEL_TYPE_CLOTHES,
  busy = false,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('workbench');
  const isEdit = Boolean(initial?.modelId || initial?.id);
  const allowTypeChange = officialMode || !isEdit;
  const [modelName, setModelName] = useState('');
  const [modelType, setModelType] = useState(MODEL_TYPE_CLOTHES);
  const [portraitSlot, setPortraitSlot] = useState(null);
  const [height, setHeight] = useState('');
  const [bust, setBust] = useState('');
  const [waist, setWaist] = useState('');
  const [hipline, setHipline] = useState('');
  const [footLength, setFootLength] = useState('');
  const [footWidth, setFootWidth] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const clothes = pickClothes(initial);
    const shoe = pickShoe(initial);
    setModelName(String(initial?.name || initial?.modelName || initial?.payload?.modelName || '').trim());
    if (isEdit) {
      setModelType(resolveModelType(initial));
    } else {
      const raw = Number(defaultModelType);
      setModelType(raw === MODEL_TYPE_SHOE ? MODEL_TYPE_SHOE : MODEL_TYPE_CLOTHES);
    }
    setPortraitSlot(portraitSlotFromUrl(portraitUrlOf(initial)));
    setHeight(clothes.height);
    setBust(clothes.bust);
    setWaist(clothes.waist);
    setHipline(clothes.hipline);
    setFootLength(shoe.footLength);
    setFootWidth(shoe.footWidth);
    setFieldErrors({});
  }, [open, initial, isEdit, defaultModelType]);

  if (!open) return null;

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  const submit = async () => {
    const nextErrors = validateModelForm({
      modelName,
      modelType,
      height,
      bust,
      waist,
      hipline,
      footLength,
      footWidth,
    });
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    const name = String(modelName || '').trim();
    const type = Number(modelType);
    const body = {
      modelName: name,
      modelType: type,
    };
    const portrait = officialMode ? slotPortraitUrl(portraitSlot) : slotTemuUrl(portraitSlot);
    if (portrait) body.headPortrait = portrait;

    if (type === MODEL_TYPE_CLOTHES) {
      body.clothesModel = {
        height: String(height || '').trim(),
        bust: String(bust || '').trim(),
        waist: String(waist || '').trim(),
        hipline: String(hipline || '').trim(),
      };
    } else {
      body.shoeModel = {
        footLength: String(footLength || '').trim(),
        footWidth: String(footWidth || '').trim(),
      };
    }

    if (isEdit) {
      body.id = initial?.modelId || initial?.id;
    }

    setFieldErrors({});
    try {
      await onSubmit?.(body);
    } catch (e) {
      const mapped = mapModelErrorToFields(
        toIpeUserErrorMessage(e, e?.message || t('modelLib.saveFailed')),
      );
      setFieldErrors(mapped);
    }
  };

  return (
    <div className="tpo-batch-modal" role="dialog" aria-modal="true" aria-labelledby="tpo-model-tpl-title">
      <div className="tpo-batch-modal__backdrop" aria-hidden="true" onClick={busy ? undefined : onClose} />
      <div className="tpo-batch-modal__panel">
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-model-tpl-title">
            {isEdit
              ? (officialMode ? t('modelLib.editOfficial') : t('modelLib.editModel'))
              : (officialMode ? t('modelLib.createOfficial') : t('modelLib.create'))}
          </h2>
          <button
            type="button"
            className="tpo-batch-modal__close"
            disabled={busy}
            onClick={onClose}
            aria-label={t('moduleShell.close')}
          >
            ×
          </button>
        </div>
        <div className="tpo-batch-modal__body">
          <div className="tpo-model-form">
            {fieldErrors.form ? (
              <div className="tpo-field__error" role="alert">{fieldErrors.form}</div>
            ) : null}

            <label className={`tpo-discuss-field${fieldErrors.modelName ? ' tpo-field--invalid' : ''}`}>
              <span>
                {t('modelLib.colName')}
                <RequiredMark />
              </span>
              <input
                className="tpo-input"
                value={modelName}
                disabled={busy}
                placeholder={t('modelLib.namePhTemu')}
                maxLength={64}
                aria-invalid={Boolean(fieldErrors.modelName)}
                onChange={(e) => {
                  setModelName(e.target.value);
                  clearFieldError('modelName');
                }}
              />
              <FieldError message={fieldErrors.modelName} />
            </label>

            <div
              className={`tpo-discuss-field${fieldErrors.modelType ? ' tpo-field--invalid' : ''}`}
              role="group"
              aria-label={t('modelLib.type')}
            >
              <span>
                {t('modelLib.type')}
                <RequiredMark />
              </span>
              <div className="tpo-model-form__type-row">
                <label className="tpo-model-form__radio">
                  <input
                    type="radio"
                    name="modelType"
                    checked={modelType === MODEL_TYPE_CLOTHES}
                    disabled={busy || !allowTypeChange}
                    onChange={() => {
                      setModelType(MODEL_TYPE_CLOTHES);
                      clearFieldError('modelType');
                    }}
                  />
                  {t('modelLib.clothes')}
                </label>
                <label className="tpo-model-form__radio">
                  <input
                    type="radio"
                    name="modelType"
                    checked={modelType === MODEL_TYPE_SHOE}
                    disabled={busy || !allowTypeChange}
                    onChange={() => {
                      setModelType(MODEL_TYPE_SHOE);
                      clearFieldError('modelType');
                    }}
                  />
                  {t('modelLib.shoeTemu')}
                </label>
              </div>
              {isEdit && !officialMode ? (
                <p className="tpo-wizard__inline-hint">{t('modelLib.typeLocked')}</p>
              ) : null}
              <FieldError message={fieldErrors.modelType} />
            </div>

            <div className={`tpo-discuss-field tpo-model-form__portrait${fieldErrors.headPortrait ? ' tpo-field--invalid' : ''}`}>
              <span className="tpo-field__label-with-help">
                {t('modelLib.avatar')}
                <FieldHelpMark
                  text={officialMode
                    ? t('modelLib.avatarHintOfficial')
                    : t('modelLib.avatarHintTemu')}
                />
              </span>
              <PublishImageUploadZone
                compact
                labelInside
                label={t('modelLib.uploadAvatar')}
                imageRule="material"
                imageType={1}
                imageBizType={0}
                multiple={false}
                maxCount={1}
                shopId={shopId}
                skipTemuConvert={officialMode}
                enableImageSpacePick={!officialMode}
                value={portraitSlot}
                onChange={(next) => {
                  const item = Array.isArray(next) ? (next[0] || null) : (next || null);
                  setPortraitSlot(item);
                  clearFieldError('headPortrait');
                }}
              />
              <FieldError message={fieldErrors.headPortrait} />
            </div>

            {modelType === MODEL_TYPE_CLOTHES ? (
              <>
                <label className={`tpo-discuss-field${fieldErrors.height ? ' tpo-field--invalid' : ''}`}>
                  <span>
                    {t('modelLib.heightLabel')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tpo-input"
                    value={height}
                    disabled={busy}
                    placeholder={t('modelLib.phHeight')}
                    aria-invalid={Boolean(fieldErrors.height)}
                    onChange={(e) => {
                      setHeight(e.target.value);
                      clearFieldError('height');
                    }}
                  />
                  <FieldError message={fieldErrors.height} />
                </label>
                <label className={`tpo-discuss-field${fieldErrors.bust ? ' tpo-field--invalid' : ''}`}>
                  <span>
                    {t('modelLib.bust')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tpo-input"
                    value={bust}
                    disabled={busy}
                    placeholder={t('modelLib.phBust')}
                    aria-invalid={Boolean(fieldErrors.bust)}
                    onChange={(e) => {
                      setBust(e.target.value);
                      clearFieldError('bust');
                    }}
                  />
                  <FieldError message={fieldErrors.bust} />
                </label>
                <label className={`tpo-discuss-field${fieldErrors.waist ? ' tpo-field--invalid' : ''}`}>
                  <span>
                    {t('modelLib.waist')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tpo-input"
                    value={waist}
                    disabled={busy}
                    placeholder={t('modelLib.phWaist')}
                    aria-invalid={Boolean(fieldErrors.waist)}
                    onChange={(e) => {
                      setWaist(e.target.value);
                      clearFieldError('waist');
                    }}
                  />
                  <FieldError message={fieldErrors.waist} />
                </label>
                <label className={`tpo-discuss-field${fieldErrors.hipline ? ' tpo-field--invalid' : ''}`}>
                  <span>
                    {t('modelLib.hip')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tpo-input"
                    value={hipline}
                    disabled={busy}
                    placeholder={t('modelLib.phHip')}
                    aria-invalid={Boolean(fieldErrors.hipline)}
                    onChange={(e) => {
                      setHipline(e.target.value);
                      clearFieldError('hipline');
                    }}
                  />
                  <FieldError message={fieldErrors.hipline} />
                </label>
              </>
            ) : (
              <>
                <label className={`tpo-discuss-field${fieldErrors.footLength ? ' tpo-field--invalid' : ''}`}>
                  <span>
                    {t('modelLib.footLength')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tpo-input"
                    value={footLength}
                    disabled={busy}
                    placeholder={t('modelLib.phFootLen')}
                    aria-invalid={Boolean(fieldErrors.footLength)}
                    onChange={(e) => {
                      setFootLength(e.target.value);
                      clearFieldError('footLength');
                    }}
                  />
                  <FieldError message={fieldErrors.footLength} />
                </label>
                <label className={`tpo-discuss-field${fieldErrors.footWidth ? ' tpo-field--invalid' : ''}`}>
                  <span>
                    {t('modelLib.footWidth')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tpo-input"
                    value={footWidth}
                    disabled={busy}
                    placeholder={t('modelLib.phFootW')}
                    aria-invalid={Boolean(fieldErrors.footWidth)}
                    onChange={(e) => {
                      setFootWidth(e.target.value);
                      clearFieldError('footWidth');
                    }}
                  />
                  <FieldError message={fieldErrors.footWidth} />
                </label>
              </>
            )}
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary" disabled={busy} onClick={onClose}>
            {t('shop.cancel')}
          </button>
          <button type="button" className="tpo-btn tpo-btn--primary" disabled={busy} onClick={() => { void submit(); }}>
            {busy ? t('tiktokPublish.saving') : (officialMode ? t('shop.save') : t('modelLib.savePlatform'))}
          </button>
        </div>
      </div>
    </div>
  );
}
